import get from 'lodash/get'
import { useTranslation } from 'react-i18next'

import { CallType } from 'lib/_types'

import {
  useGetCallDetailsQuery,
  useGetRTMTokenMutation,
} from 'store/api/yay/call'
import { useCurrentUser } from 'store/api/yay/user'

import useOpenConferenceCall from 'features/ConferenceCall/hooks/useConferenceCall'

import Avatar from 'components/Avatar'
import Button from 'components/Button'
import Modal from 'components/Modal/Modal'
import { rtmClient } from 'features/ConferenceCall/hooks/useAgora'

const IncomingCallNotification = ({
  onClose,
  isActive,
  callNotification,
}: {
  onClose: Function
  isActive: boolean
  callNotification: {
    conference_call_id: string
    user_nickname: string
    user_icon: string
    type: 'invite_call' | 'invite_video_call'
  }
}) => {
  const currentUserId = useCurrentUser()?.data?.id
  const conferenceId = callNotification?.conference_call_id
  const conferenceCall = useGetCallDetailsQuery(
    { conferenceId },
    { skip: !isActive || !conferenceId }
  )
  const { t } = useTranslation()
  const { openConferenceCall } = useOpenConferenceCall()
  const [getRTMToken] = useGetRTMTokenMutation()

  const onAcceptCall = () => {
    openConferenceCall({ conferenceCallId: conferenceId })
    onClose()
  }

  const onRejectCall = async () => {
    onClose()

    const shouldSendRejectSignal = [
      'invite_call',
      'invite_video_call',
    ].includes(callNotification.type)

    if (!shouldSendRejectSignal) return
    // join rtm and leave to make caller leave call
    try {
      const { token } = await getRTMToken({ conferenceId }).unwrap()
      const uid = (currentUserId || '').toString()

      await rtmClient.login({ token, uid })
      const rtmChannel = rtmClient.createChannel(
        conferenceCall?.data?.agora_channel
      )
      await rtmChannel.join()
      await rtmChannel.leave()
      await rtmClient.logout()
    } catch (e) {
      console.error('cannot reject call', e)
    }
  }

  return (
    <Modal
      className="PushNotification__call_modal"
      title={t('incoming_call_notification_modal.title')}
      onClose={onRejectCall}
      isActive={isActive && !conferenceCall.isLoading}
      borderless
      autoHeight
    >
      <div className="PushNotification__call_modal__wrapper">
        <div className="PushNotification__call_modal__content">
          <Avatar
            wrapperClassName="PushNotification__call_modal__content__image"
            user={{
              profile_icon_thumbnail: get(callNotification, 'user_icon', ''),
            }}
          />
          <div className="PushNotification__call_modal__content__title">
            {t('incoming_call_notification_modal.caller_name', {
              nickname: get(callNotification, 'user_nickname', ''),
            })}
          </div>
          <div className="PushNotification__call_modal__content__detail">
            {t('incoming_call_notification_modal.detail')}
          </div>
        </div>
        <div className="PushNotification__call_modal__footer">
          <Button buttonStyle="ghost" onClick={onRejectCall}>
            {t('incoming_call_notification_modal.reject')}
          </Button>
          <Button
            onClick={onAcceptCall}
            {...(get(conferenceCall, 'data.call_type') === CallType.Video
              ? {
                  icon: 'video-call',
                  buttonStyle: 'blue',
                }
              : { icon: 'voice-call', buttonStyle: 'green' })}
          >
            {t('incoming_call_notification_modal.accept')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default IncomingCallNotification
